import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  History,
  LayoutGrid,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/components/layout/page-header';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { usePermission } from '@/features/auth/auth.store';
import { cn } from '@/lib/utils';
import { ColorField, Field, ImageField, LogsSheet, MenuTile } from './catalog-shared';
import { BreakdownSheet } from './breakdown-sheet';
import {
  useDeleteDepartment,
  useDepartments,
  useSaveDepartment,
  type Department,
  type DepartmentInput,
} from './catalog.api';

/**
 * Departments — the top level of the storefront menu.
 *
 * The list leads with the tile as customers see it: an image on a background
 * inside a border, both set here as hex. Two colour fields say nothing about
 * the result, so the editor shows the same tile updating as they change.
 */

const EMPTY: DepartmentInput = {
  name: '',
  nameEng: '',
  imageUrl: '',
  color: '#ffffff',
  borderColor: '#ffffff',
  ordering: 0,
};

export function DepartmentsPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage ?? 'en') === 'en';
  const isAdmin = usePermission('isSuperAdmin');

  const list = useDepartments();
  const save = useSaveDepartment();
  const remove = useDeleteDepartment();

  const [editing, setEditing] = useState<DepartmentInput | null>(null);
  const [logsFor, setLogsFor] = useState<Department | null>(null);
  const [breakdownOpen, setBreakdownOpen] = useState(false);

  const rows = useMemo(() => {
    const data = list.data ?? [];
    // Ordering is what the storefront honours; name breaks the ties.
    return [...data].sort(
      (a, b) =>
        (a.ordering ?? 0) - (b.ordering ?? 0) ||
        (isEnglish ? a.nameEng : a.name)?.localeCompare(
          (isEnglish ? b.nameEng : b.name) ?? ''
        ) ||
        0
    );
  }, [list.data, isEnglish]);

  const isEdit = !!editing?.id;
  const name = (row: Department) => (isEnglish ? row.nameEng : row.name) || '—';
  const set = (patch: Partial<DepartmentInput>) =>
    setEditing((current) => (current ? { ...current, ...patch } : current));

  const failed = () => toast.error(t('catalog.actionFailed'));

  function submit() {
    save.mutate(editing!, {
      onSuccess: () => {
        toast.success(t('catalog.saved'));
        setEditing(null);
      },
      onError: failed,
    });
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('catalog.departmentsTitle')}
        description={t('catalog.departmentsSubtitle')}
        actions={
          <>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setBreakdownOpen(true)}
            >
              <LayoutGrid />
              <span className="hidden sm:inline">
                {t('catalog.viewBreakdown')}
              </span>
            </Button>
            <Button
              variant="outline"
              size="icon-sm"
              aria-label={t('catalog.reload')}
              disabled={list.isFetching}
              onClick={() => void list.refetch()}
            >
              <RefreshCw className={list.isFetching ? 'animate-spin' : ''} />
            </Button>
            <Button size="sm" onClick={() => setEditing(EMPTY)}>
              <Plus />
              <span className="hidden sm:inline">{t('catalog.createNew')}</span>
            </Button>
          </>
        }
      />

      {list.isError && (
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <div className="space-y-2">
            <p>{t('catalog.loadFailed')}</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => void list.refetch()}
            >
              {t('common.retry')}
            </Button>
          </div>
        </div>
      )}

      {list.isPending && (
        <div className="divide-y rounded-xl border">
          {Array.from({ length: 6 }).map((_, index) => (
            <div key={index} className="flex items-center gap-3 p-3">
              <Skeleton className="size-12 shrink-0 rounded-lg" />
              <Skeleton className="h-4 w-48" />
            </div>
          ))}
        </div>
      )}

      {list.isSuccess && rows.length === 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <p className="text-muted-foreground rounded-xl border border-dashed py-14 text-center text-sm">
            {t('catalog.noDepartments')}
          </p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="relative rounded-xl">
          <BusyOverlay show={list.isFetching} />
          <div
            className={cn(
              'divide-y rounded-xl border',
              list.isFetching && 'opacity-60 transition-opacity'
            )}
          >
            {rows.map((row) => (
              <div
                key={row.id}
                className="flex flex-col gap-3 p-3 sm:flex-row sm:items-center"
              >
                <div className="flex min-w-0 flex-1 items-center gap-3">
                  <MenuTile
                    size="sm"
                    imageUrl={row.imageUrl}
                    color={row.color}
                    borderColor={row.borderColor}
                  />
                  <div className="min-w-0">
                    <p className="truncate font-medium">{name(row)}</p>
                    <p className="text-muted-foreground truncate text-xs">
                      {isEnglish ? row.name : row.nameEng}
                    </p>
                    <p className="text-muted-foreground text-xs">
                      {t('catalog.ordering')}: {row.ordering ?? 0}
                    </p>
                  </div>
                </div>

                <div className="flex shrink-0 flex-wrap gap-2">
                  {isAdmin && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setLogsFor(row)}
                    >
                      <History className="size-3.5" />
                      {t('catalog.logs')}
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditing(row)}
                  >
                    <Pencil className="size-3.5" />
                    {t('catalog.edit')}
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon-sm"
                        className="text-destructive"
                        aria-label={t('catalog.delete')}
                        disabled={remove.isPending}
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>{t('catalog.delete')}</AlertDialogTitle>
                        <AlertDialogDescription>
                          {t('catalog.deleteDepartmentPrompt', {
                            name: name(row),
                          })}
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                          onClick={() =>
                            remove.mutate(row.id, {
                              onSuccess: () => toast.success(t('catalog.deleted')),
                              onError: failed,
                            })
                          }
                        >
                          {t('common.yes')}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ------------------------------------------------------- editor */}
      <Sheet
        open={editing !== null}
        onOpenChange={(next) => {
          if (!next) setEditing(null);
        }}
      >
        <SheetContent
          side="right"
          className="w-full gap-0 overflow-y-auto p-0 sm:max-w-md"
        >
          <SheetHeader className="border-b pr-12">
            <SheetTitle>
              {isEdit
                ? t('catalog.editDepartment')
                : t('catalog.newDepartment')}
            </SheetTitle>
          </SheetHeader>

          {editing && (
            <div className="space-y-4 p-4">
              {/* The result, not the two hex values that make it. */}
              <div className="flex justify-center rounded-xl border p-4">
                <MenuTile
                  imageUrl={editing.imageUrl}
                  color={editing.color}
                  borderColor={editing.borderColor}
                />
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label={`${t('catalog.name')} (EN)`}>
                  <Input
                    value={editing.nameEng ?? ''}
                    onChange={(e) => set({ nameEng: e.target.value })}
                  />
                </Field>
                <Field label={`${t('catalog.name')} (ES)`}>
                  <Input
                    value={editing.name ?? ''}
                    onChange={(e) => set({ name: e.target.value })}
                  />
                </Field>
              </div>

              <Field
                label={t('catalog.ordering')}
                hint={t('catalog.orderingHint')}
              >
                <Input
                  type="number"
                  value={editing.ordering ?? 0}
                  onChange={(e) => set({ ordering: Number(e.target.value) })}
                />
              </Field>

              <div className="grid gap-4 sm:grid-cols-2">
                <ColorField
                  label={t('catalog.color')}
                  value={editing.color}
                  onChange={(color) => set({ color })}
                />
                <ColorField
                  label={t('catalog.borderColor')}
                  value={editing.borderColor}
                  onChange={(borderColor) => set({ borderColor })}
                />
              </div>

              <ImageField
                label={t('catalog.image')}
                hint={t('catalog.imageRequirements')}
                value={editing.imageUrl}
                onChange={(imageUrl) => set({ imageUrl })}
              />

              <div className="flex justify-end gap-2 pt-1">
                <Button
                  variant="outline"
                  disabled={save.isPending}
                  onClick={() => setEditing(null)}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  disabled={
                    save.isPending ||
                    (!editing.nameEng?.trim() && !editing.name?.trim())
                  }
                  onClick={submit}
                >
                  {save.isPending && <Loader2 className="animate-spin" />}
                  {isEdit ? t('common.save') : t('catalog.create')}
                </Button>
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>

      <LogsSheet
        departmentId={logsFor?.id}
        title={logsFor ? name(logsFor) : t('catalog.logs')}
        open={logsFor !== null}
        onOpenChange={(next) => {
          if (!next) setLogsFor(null);
        }}
      />

      <BreakdownSheet open={breakdownOpen} onOpenChange={setBreakdownOpen} />
    </div>
  );
}
